/**
 * `ActionMenu` — LE menu « ⋮ » à libellés (LOT-RT-DA2, DA-050 / DA-027).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Ce menu vivait DANS `DataTable` (H5, `RowActionMenu`) : c'est lui qui a
 * sorti la corbeille muette des tableaux (DA-027). Mais les LISTES DE CARTES
 * (`/contacts`, `/messaging/templates`, `/messaging/providers`) gardaient
 * leurs corbeilles en icônes de 32 px — et la recette passe 2 a mesuré que
 * leurs zones tactiles de 44 px SE CHEVAUCHENT : 32 px dessinés + 8 px d'écart
 * = 40 < 44, et le point MILIEU de l'espace entre « Modifier » et
 * « Supprimer » appartenait à « Supprimer » (DA-050). Sur un iPad, viser le
 * crayon pouvait déclencher la suppression.
 *
 * Le correctif ratifié par la recette est le patron qui existait déjà sur
 * `/admin/twilio-numbers` : l'action DESTRUCTIVE sort du rang des icônes et
 * passe dans un menu où son nom est du TEXTE. Ce fichier extrait ce patron
 * pour que cartes et tableaux consomment le même.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CE MENU N'EST PAS UNE MODALE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Pas de voile, pas de piège de focus, il ne bloque rien. Il se ferme au clic
 * extérieur, à Échap, au défilement et au redimensionnement — un menu ancré à
 * un bouton qui a bougé est un menu perdu.
 *
 * Rendu dans un PORTAIL sur `document.body`, en `position: fixed` : les
 * conteneurs de tableau portent `overflow-x-auto` et les cartes peuvent vivre
 * dans des grilles rognantes — un menu en `absolute` y serait coupé.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical, type LucideIcon } from 'lucide-react'

import { IconButton } from './IconButton'
import type { ButtonSize } from './Button'

export interface ActionMenuItem {
  id: string
  /** Nom de l'action. Toujours obligatoire — c'est le nom accessible ET le libellé du menu. */
  label: string
  icon: LucideIcon
  onClick: () => void
  disabled?: boolean
  /**
   * Explication sous le libellé (texte, jamais une infobulle : un iPad ne
   * survole pas) — typiquement POURQUOI l'action est désactivée. Reliée par
   * `aria-describedby` : le nom accessible reste le seul libellé.
   */
  hint?: string
  loading?: boolean
  /**
   * Action destructive. Elle n'est JAMAIS rendue en icône nue : sa place est
   * ici, où son nom est du texte (§5, DA-027). C'est un booléen et non une
   * variante de couleur — la couleur suit, elle ne décide pas.
   */
  destructive?: boolean
}

export interface ActionMenuProps {
  actions: ActionMenuItem[]
  /** Nom accessible du déclencheur (« Autres actions »). */
  label: string
  /**
   * Taille du déclencheur. `md` (44 px, dessin = cible) par défaut — le cas
   * des tableaux. `sm` (32 px dessinés, cible de 44 px par pseudo-élément)
   * pour les rangées d'icônes compactes des cartes : dans ce cas le conteneur
   * DOIT être un `IconButtonGroup`, dont l'écart de 12 px garantit que deux
   * cibles de 44 px ne se recouvrent jamais (DA-050).
   */
  size?: ButtonSize
}

export function ActionMenu({ actions, label, size = 'md' }: ActionMenuProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const hintPrefix = useId()

  // Deux passes, volontairement : la première pose le menu sous le bouton
  // (hauteur encore inconnue), la seconde — déclenchée par le changement de
  // `position` — le mesure et le BASCULE au-dessus s'il débordait du bas de la
  // fenêtre. Sans elle, « Supprimer » sur la dernière ligne d'un tableau plein
  // écran s'ouvrirait hors de l'écran. La garde d'identité arrête la boucle dès
  // que la position est stable.
  useLayoutEffect(() => {
    if (!open) return
    const trigger = triggerRef.current?.getBoundingClientRect()
    if (!trigger) return

    const height = menuRef.current?.getBoundingClientRect().height ?? 0
    const below = trigger.bottom + 4
    const top =
      height > 0 && below + height > window.innerHeight - 8
        ? Math.max(8, trigger.top - height - 4)
        : below

    setPosition((previous) =>
      previous && previous.top === top && previous.left === trigger.right
        ? previous
        : { top, left: trigger.right }
    )
  }, [open, position])

  useEffect(() => {
    if (!open) return

    const close = () => setOpen(false)
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    // `true` : on capte AUSSI le défilement d'un conteneur interne, qui ne
    // remonte pas jusqu'à `window` en phase de bouillonnement.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <>
      <IconButton
        ref={triggerRef}
        icon={MoreVertical}
        label={label}
        size={size}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((value) => {
            // La position repart de zéro à chaque ouverture : réutiliser celle
            // du dernier affichage rejouerait une bascule calculée pour une
            // autre position de défilement.
            if (!value) setPosition(null)
            return !value
          })
        }}
      />
      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            style={{ top: position.top, left: position.left }}
            className="fixed z-50 min-w-[12rem] -translate-x-full overflow-hidden rounded-card border border-border-secondary bg-background-primary py-1 shadow-xl"
          >
            {actions.map((action) => {
              const Icon = action.icon
              const hintId = action.hint ? `${hintPrefix}-${action.id}-hint` : undefined
              return (
                <button
                  key={action.id}
                  type="button"
                  role="menuitem"
                  // Le nom accessible reste le libellé seul : l'explication est une DESCRIPTION.
                  aria-label={action.hint ? action.label : undefined}
                  aria-describedby={hintId}
                  disabled={action.disabled || action.loading}
                  onClick={(event) => {
                    event.stopPropagation()
                    setOpen(false)
                    action.onClick()
                  }}
                  className={[
                    'flex min-h-touch w-full items-center gap-2 px-3 text-left text-t4',
                    'transition-colors hover:bg-background-secondary',
                    'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
                    // La couleur SUIT le rôle de l'action, elle ne le décide pas :
                    // un contour/texte `danger` (§5), jamais un aplat rouge répété
                    // à chaque ligne.
                    action.destructive ? 'text-danger-400' : 'text-foreground',
                  ].join(' ')}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {action.hint ? (
                    <span className="flex min-w-0 flex-col py-1.5">
                      <span>{action.label}</span>
                      <span id={hintId} className="text-t5 text-foreground-secondary">
                        {action.hint}
                      </span>
                    </span>
                  ) : (
                    action.label
                  )}
                </button>
              )
            })}
          </div>,
          document.body
        )}
    </>
  )
}

export default ActionMenu
